/**
 * WebhookRuleDetailDialog - Modal de detalhes para regras de webhook
 * 
 * Modal com 3 abas: Geral, Logs e Estatísticas.
 * Similar ao WorkspaceWebhookConfig do CRM.
 * 
 * @version 1.0.0
 * @date 2026-01-30
 */

import { useState, useEffect } from 'react';
import { ArrowLeft, Settings2, List, BarChart3, Loader2, Trash2 } from 'lucide-react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { ConfirmDialog } from '@/components/ui/confirm-dialog';
import { InboxSelector } from './InboxSelector';
import { WebhookConditionConfig } from './WebhookConditionConfig';
import { RoutingActionBuilder } from './RoutingActionBuilder';
import { WebhookRuleLogsTab } from './WebhookRuleLogsTab';
import { WebhookRuleStatsTab } from './WebhookRuleStatsTab';
import { createLogger } from '@/lib/logger';
import type { RoutingRuleWithWebhook, RoutingActionItem } from './types';
import type { RoutingActionType } from '@/pages/tenant/RoutingRules';

// Logger para depuração
const logger = createLogger('WebhookRuleDetailDialog');

// ============= INTERFACES =============

interface FieldMapping {
  contact_phone: string;
  contact_name: string;
}

interface WebhookRuleDetailDialogProps {
  /** Se o modal está aberto */
  open: boolean;
  /** Callback para fechar o modal */
  onOpenChange: (open: boolean) => void;
  /** Regra a ser editada */
  rule: RoutingRuleWithWebhook;
  /** Callback de sucesso */
  onSuccess: () => void;
  /** Callback para deletar */
  onDelete: () => void;
  /** Se pode editar */
  canEdit: boolean;
  /** Se pode deletar */
  canDelete: boolean;
}

// ============= COMPONENTE PRINCIPAL =============

export function WebhookRuleDetailDialog({
  open,
  onOpenChange,
  rule,
  onSuccess,
  onDelete,
  canEdit,
  canDelete,
}: WebhookRuleDetailDialogProps) {
  const { currentTenant } = useAuth();

  // Estados do formulário
  const [loading, setLoading] = useState(false);
  const [loadingActions, setLoadingActions] = useState(true);
  const [activeTab, setActiveTab] = useState('general');
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);

  // Campos do formulário
  const [name, setName] = useState(rule.name);
  const [description, setDescription] = useState(rule.description || '');
  const [stopOnMatch, setStopOnMatch] = useState(rule.stop_on_match);
  const [allInboxes, setAllInboxes] = useState(!rule.inbox_ids || rule.inbox_ids.length === 0);
  const [selectedInboxIds, setSelectedInboxIds] = useState<string[]>(rule.inbox_ids || []);
  const [actions, setActions] = useState<RoutingActionItem[]>([]);
  const [fieldMapping, setFieldMapping] = useState<FieldMapping>(() => {
    const fm = rule.field_mapping;
    return fm
      ? { contact_phone: fm.contact_phone || 'telefone', contact_name: fm.contact_name || 'nome' }
      : { contact_phone: 'telefone', contact_name: 'nome' };
  });
  const [webhookToken, setWebhookToken] = useState(rule.webhook_token);

  // Carregar ações ao abrir
  useEffect(() => {
    if (open && rule.id) {
      loadRuleActions();
    }
  }, [open, rule.id]);

  // Atualizar valores quando regra muda
  useEffect(() => {
    setName(rule.name);
    setDescription(rule.description || '');
    setStopOnMatch(rule.stop_on_match);
    setAllInboxes(!rule.inbox_ids || rule.inbox_ids.length === 0);
    setSelectedInboxIds(rule.inbox_ids || []);
    setWebhookToken(rule.webhook_token);

    const fm = rule.field_mapping;
    setFieldMapping(
      fm
        ? { contact_phone: fm.contact_phone || 'telefone', contact_name: fm.contact_name || 'nome' }
        : { contact_phone: 'telefone', contact_name: 'nome' }
    );
  }, [rule]);

  // Carrega ações via payload já hidratado pelo backend (rule.actions[])
  const loadRuleActions = async () => {
    setLoadingActions(true);
    try {
      const embedded = ((rule as any).actions || []) as Array<any>;

      if (embedded.length > 0) {
        const loadedActions: RoutingActionItem[] = embedded.map((item, index) => ({
          id: item.id,
          action_type: item.action_type as RoutingActionType,
          action_value: item.action_value as Record<string, unknown>,
          sort_order: item.sort_order ?? index,
          delay_seconds: item.delay_seconds || 0,
        }));
        setActions(loadedActions);
      } else {
        setActions([
          {
            id: crypto.randomUUID(),
            action_type: rule.action_type as RoutingActionType,
            action_value: rule.action_value as Record<string, unknown>,
            sort_order: 0,
            delay_seconds: 0,
          },
        ]);
      }
    } catch (error) {
      logger.error('Erro ao carregar ações', error);
      setActions([]);
    } finally {
      setLoadingActions(false);
    }
  };

  // Salvar alterações
  const handleSubmit = async () => {
    if (!name.trim()) {
      toast.error('Nome é obrigatório');
      return;
    }

    if (!allInboxes && selectedInboxIds.length === 0) {
      toast.error('Selecione pelo menos uma inbox');
      return;
    }

    if (actions.length === 0) {
      toast.error('Adicione pelo menos uma ação');
      return;
    }

    setLoading(true);

    try {
      const firstAction = actions[0];

      // Atualiza regra + actions atomicamente via RPC (invalida trigger_cache internamente)
      const { error: ruleError } = await supabase.rpc('update_routing_rule', {
        p_rule_id: rule.id,
        p_payload: {
          name: name.trim(),
          description: description.trim() || null,
          action_type: firstAction.action_type,
          action_value: firstAction.action_value as Record<string, unknown>,
          stop_on_match: stopOnMatch,
          inbox_ids: allInboxes ? null : selectedInboxIds,
          field_mapping: fieldMapping as unknown as Record<string, unknown>,
        } as any,
        p_actions: actions.map((action, index) => ({
          action_type: action.action_type,
          action_value: action.action_value,
          sort_order: index,
          delay_seconds: action.delay_seconds || 0,
        })) as any,
        p_tenant_hint: currentTenant?.id,
      });

      if (ruleError) throw ruleError;

      toast.success('Webhook atualizado com sucesso');
      onSuccess();
    } catch (error) {
      logger.error('Erro ao atualizar webhook', error);
      toast.error('Erro ao atualizar webhook');
    } finally {
      setLoading(false);
    }
  };

  // Confirmar exclusão
  const handleConfirmDelete = () => {
    setShowDeleteConfirm(false);
    onDelete();
  };

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="max-w-4xl h-[90vh] flex flex-col overflow-hidden">
          <DialogHeader className="shrink-0">
            <DialogTitle className="flex items-center gap-2">
              <Settings2 className="h-5 w-5" />
              {rule.name}
            </DialogTitle>
            <DialogDescription>
              Gerencie as configurações, logs e estatísticas do webhook.
            </DialogDescription>
          </DialogHeader>

          {/* Abas */}
          <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
            <TabsList className="grid w-full grid-cols-3 shrink-0">
              <TabsTrigger value="general" className="flex items-center gap-2">
                <Settings2 className="h-4 w-4" />
                Geral
              </TabsTrigger>
              <TabsTrigger value="logs" className="flex items-center gap-2">
                <List className="h-4 w-4" />
                Logs
              </TabsTrigger>
              <TabsTrigger value="stats" className="flex items-center gap-2">
                <BarChart3 className="h-4 w-4" />
                Estatísticas
              </TabsTrigger>
            </TabsList>

            {/* Conteúdo das abas */}
            <div className="flex-1 min-h-0 mt-4">
              {/* Aba Geral */}
              <TabsContent value="general" className="h-full m-0">
                <ScrollArea className="h-full pr-4">
                  <div className="space-y-6 py-2">
                    {/* Nome e Descrição */}
                    <div className="grid grid-cols-1 gap-4">
                      <div className="space-y-2">
                        <Label htmlFor="name">Nome *</Label>
                        <Input
                          id="name"
                          value={name}
                          onChange={(e) => setName(e.target.value)}
                          placeholder="Nome do webhook"
                          disabled={!canEdit}
                        />
                      </div>
                      <div className="space-y-2">
                        <Label htmlFor="description">Descrição</Label>
                        <Textarea
                          id="description"
                          value={description}
                          onChange={(e) => setDescription(e.target.value)}
                          placeholder="Descrição opcional..."
                          rows={2}
                          disabled={!canEdit}
                        />
                      </div>
                    </div>

                    {/* Seleção de Inboxes */}
                    <div className="space-y-4 p-4 border rounded-lg">
                      <h3 className="font-medium">Inboxes</h3>
                      <InboxSelector
                        allInboxes={allInboxes}
                        selectedInboxIds={selectedInboxIds}
                        onAllInboxesChange={setAllInboxes}
                        onSelectedIdsChange={setSelectedInboxIds}
                        showValidation={false}
                      />
                    </div>

                    {/* Configuração de Webhook */}
                    <div className="space-y-4 p-4 border rounded-lg border-rose-200 dark:border-rose-800 bg-rose-50/50 dark:bg-rose-950/20">
                      <h3 className="font-medium text-rose-700 dark:text-rose-300">
                        Endpoint do Webhook
                      </h3>
                      <WebhookConditionConfig
                        webhookToken={webhookToken}
                        ruleId={rule.id}
                        fieldMapping={fieldMapping}
                        onFieldMappingChange={setFieldMapping}
                        onTokenRegenerated={setWebhookToken}
                        isCreating={false}
                      />
                    </div>

                    {/* Sequência de Ações */}
                    <div className="space-y-4 p-4 border rounded-lg">
                      <h3 className="font-medium">Ações a Executar</h3>
                      {loadingActions ? (
                        <div className="flex items-center justify-center py-8">
                          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
                        </div>
                      ) : (
                        <RoutingActionBuilder
                          items={actions}
                          onItemsChange={setActions}
                          selectedInboxIds={allInboxes ? [] : selectedInboxIds}
                        />
                      )}
                    </div>

                    {/* Opções */}
                    <div className="flex items-center justify-between p-4 border rounded-lg">
                      <div>
                        <Label htmlFor="stop-on-match">Parar ao dar match</Label>
                        <p className="text-sm text-muted-foreground">
                          Se ativado, não verifica as próximas regras
                        </p>
                      </div>
                      <Switch
                        id="stop-on-match"
                        checked={stopOnMatch}
                        onCheckedChange={setStopOnMatch}
                        disabled={!canEdit}
                      />
                    </div>
                  </div>
                </ScrollArea>
              </TabsContent>

              {/* Aba Logs */}
              <TabsContent value="logs" className="h-full m-0">
                <ScrollArea className="h-full pr-4">
                  <WebhookRuleLogsTab ruleId={rule.id} />
                </ScrollArea>
              </TabsContent>

              {/* Aba Estatísticas */}
              <TabsContent value="stats" className="h-full m-0">
                <ScrollArea className="h-full pr-4">
                  <WebhookRuleStatsTab ruleId={rule.id} />
                </ScrollArea>
              </TabsContent>
            </div>
          </Tabs>

          {/* Footer */}
          <DialogFooter className="pt-4 shrink-0 border-t mt-auto flex justify-between">
            <div>
              {canDelete && (
                <Button
                  variant="ghost"
                  onClick={() => setShowDeleteConfirm(true)}
                  className="text-destructive hover:text-destructive hover:bg-destructive/10"
                >
                  <Trash2 className="h-4 w-4 mr-2" />
                  Excluir
                </Button>
              )}
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => onOpenChange(false)} disabled={loading}>
                Cancelar
              </Button>
              {canEdit && activeTab === 'general' && (
                <Button onClick={handleSubmit} disabled={loading || loadingActions}>
                  {loading ? (
                    <>
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                      Salvando...
                    </>
                  ) : (
                    'Salvar'
                  )}
                </Button>
              )}
            </div>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dialog de confirmação de exclusão */}
      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={setShowDeleteConfirm}
        title="Excluir webhook?"
        description={`Tem certeza que deseja excluir o webhook "${rule.name}"? Esta ação não pode ser desfeita.`}
        confirmText="Excluir"
        cancelText="Cancelar"
        onConfirm={handleConfirmDelete}
        variant="danger"
      />
    </>
  );
}
